import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import {
  CHECKOUT_DECLINE_MESSAGES,
  CHECKOUT_DECLINE_TITLE,
  CHECKOUT_RETURN_COPY,
  CHECKOUT_RETURN_PARAMS,
  type CheckoutDeclineReason,
} from '@workspace/global-utils';
import { toast } from '@workspace/ui/components/sonner';
import { authenticatedNextFetch } from '@/api/api-service';
import { AI_CREDITS_QUERY_KEY } from '@/hooks/billing/use-ai-credits';
import {
  collaboratorsPanelRequestAtom,
  objectAtomFamily,
  plansModalOpenAtom,
} from '@/jotai/atom';
import { atomKeys } from '@/jotai/atom-keys';
import { creditTopUpModalOpenAtom } from '@/lib/credit-guard';
import { Config } from '@/utils/config';

type CheckoutReturn = {
  state: 'succeeded' | 'failed' | 'pending';
  declineReason: CheckoutDeclineReason | null;
  planName: string | null;
  isPlanActive: boolean;
};

const HANDLED_KEY_PREFIX = 'checkout-return-handled:';

const wasHandled = (paymentId: string) => {
  try {
    return sessionStorage.getItem(HANDLED_KEY_PREFIX + paymentId) !== null;
  } catch {
    return false;
  }
};

const markHandled = (paymentId: string) => {
  try {
    sessionStorage.setItem(HANDLED_KEY_PREFIX + paymentId, '1');
  } catch {
    // A reload may show the message again.
  }
};

/**
 * Tells the user how a Dodo checkout went when it sends them back to the builder. Checkout returns
 * to the page it started from with `checkout=returned` (plans) or `credit_topup_returned`, plus the
 * `payment_id` Dodo added. A top-up keeps `checkout` in the URL on success because the agent's
 * paused action resumes from it.
 */
export function CheckoutReturnNotice() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const setPlansModalOpen = useSetAtom(plansModalOpenAtom);
  const setTopUpOpen = useSetAtom(creditTopUpModalOpenAtom);
  const setCollaboratorsPanelRequested = useSetAtom(collaboratorsPanelRequestAtom);
  const setProjectInfo = useSetAtom(objectAtomFamily(atomKeys.projectInfo));
  // Read once: other hooks on the page remove `checkout` while this one is still polling.
  const initialParams = useRef(searchParams);

  useEffect(() => {
    const params = initialParams.current;
    const checkout = params.get('checkout');
    const paymentId = params.get('payment_id');
    const isTopUp = checkout === 'credit_topup_returned';

    const clearParams = () =>
      setSearchParams(
        (previous) => {
          CHECKOUT_RETURN_PARAMS.forEach((key) => previous.delete(key));
          previous.delete('panel');
          return previous;
        },
        { replace: true },
      );

    if (checkout === 'cancelled' || checkout === 'credit_topup_cancelled') {
      toast(CHECKOUT_RETURN_COPY.cancelled, {
        id: 'checkout-return',
        description: CHECKOUT_RETURN_COPY.cancelledDescription,
      });
      // The agent page clears its paused top-up itself when it sees the cancel.
      if (checkout === 'cancelled') clearParams();
      return;
    }

    if (checkout !== 'returned' && checkout !== 'credit_topup_returned') return;
    if (!paymentId || wasHandled(paymentId)) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();

    const check = async () => {
      const response = await authenticatedNextFetch(
        `/api/billing/checkout-payment?paymentId=${encodeURIComponent(paymentId)}`,
        {},
        { onInsufficientCredits: 'ignore' },
      ).catch(() => null);
      const result = response?.ok ? ((await response.json()) as CheckoutReturn) : null;
      if (cancelled) return;

      if (result?.state === 'failed') {
        markHandled(paymentId);
        toast.error(CHECKOUT_DECLINE_TITLE, {
          id: 'checkout-return',
          description: CHECKOUT_DECLINE_MESSAGES[result.declineReason ?? 'declined'],
          duration: Infinity,
          action: {
            label: CHECKOUT_RETURN_COPY.tryAgain,
            onClick: () => (isTopUp ? setTopUpOpen(true) : setPlansModalOpen(true)),
          },
        });
        // Removing `checkout` also ends the page-limit card's wait and the paused top-up resume.
        clearParams();
        return;
      }

      if (isTopUp && result?.state === 'succeeded') {
        markHandled(paymentId);
        void queryClient.invalidateQueries({ queryKey: AI_CREDITS_QUERY_KEY });
        toast.success(CHECKOUT_RETURN_COPY.creditsAdded, {
          id: 'checkout-return',
          description: CHECKOUT_RETURN_COPY.creditsAddedDescription,
        });
        return;
      }

      if (!isTopUp && result?.state === 'succeeded' && result.isPlanActive) {
        markHandled(paymentId);
        const reopenCollaborators = params.get('panel') === 'collaborators';
        // Builder gates read the plan from project info, which was loaded before checkout.
        setProjectInfo((current: ProjectInfo | null) =>
          current ? { ...current, plan_name: result.planName, billing_state: 'active' } : current,
        );
        void queryClient.invalidateQueries({ queryKey: AI_CREDITS_QUERY_KEY });
        toast.success(CHECKOUT_RETURN_COPY.planActive(result.planName), {
          id: 'checkout-return',
          description: reopenCollaborators ? CHECKOUT_RETURN_COPY.collaboratorsUnlocked : undefined,
        });
        if (reopenCollaborators) setCollaboratorsPanelRequested(true);
        clearParams();
        return;
      }

      if (Date.now() - startedAt >= Config.PLAN_CHECKOUT.RETURN_POLL_TIMEOUT_MS) {
        // Not marked as handled, so refreshing the page checks again.
        const isPaid = result?.state === 'succeeded';
        toast(isPaid ? CHECKOUT_RETURN_COPY.paymentReceived : CHECKOUT_RETURN_COPY.stillConfirming, {
          id: 'checkout-return',
          description: isPaid
            ? CHECKOUT_RETURN_COPY.paymentReceivedDescription
            : isTopUp
              ? CHECKOUT_RETURN_COPY.stillConfirmingCredits
              : CHECKOUT_RETURN_COPY.stillConfirmingPlan,
          duration: Infinity,
          action: { label: CHECKOUT_RETURN_COPY.refresh, onClick: () => window.location.reload() },
        });
        return;
      }

      timer = setTimeout(check, Config.PLAN_CHECKOUT.RETURN_POLL_INTERVAL_MS);
    };

    void check();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [
    queryClient,
    setCollaboratorsPanelRequested,
    setPlansModalOpen,
    setProjectInfo,
    setSearchParams,
    setTopUpOpen,
  ]);

  return null;
}
